import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useOrg } from '../app/OrgProvider';
import { AppLink } from '../components/Nav';
import { PlayIcon } from '../components/icons';
import {
  Banner,
  Button,
  EmptyState,
  ErrorText,
  Input,
  Loading,
  PageHeader,
  Spinner,
  cx,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { timecode } from '../lib/format';
import type {
  BrandingSettings,
  BrandingSlateRef,
  BrandLogos,
  BrandTheme,
  LogoShape,
  LogoTheme,
  VideoSummary,
} from '../lib/types';
import { qk } from '../queries/keys';
import {
  fetchBranding,
  hasAnyLogo,
  inkFor,
  logoFor,
  logoProblem,
  removeLogo,
  slotKey,
  updateBranding,
  uploadLogo,
  type LogoSlot,
} from '../queries/branding';
import { fetchLibrary } from '../queries/videos';
import { SettingsTabs } from './SettingsPage';

/**
 * Settings › Branding. The team's face on everything their viewers see.
 *
 * Every section here writes through the same endpoint and is handed the whole
 * settings object back, so the page never merges a partial answer into what it
 * was already showing. `mayManage` disables the controls for a plain member
 * rather than hiding them: they can see what the team's branding is, which
 * they can see on every share page anyway.
 */

/** The page is one card per thing a team can set. */
function Section({
  title,
  description,
  children,
  aside,
}: {
  title: string;
  description: ReactNode;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="card p-4 sm:p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-h4 text-chalk">{title}</h2>
          <p className="mt-1 max-w-xl text-caption text-chalk-dim">{description}</p>
        </div>
        {aside ? <div className="shrink-0">{aside}</div> : null}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Logos
// ---------------------------------------------------------------------------

/**
 * One of the four slots: a shape, for one of the two ways a page can be lit.
 *
 * The tile behind the image is the page the logo will sit on, near-black or
 * white, and those two are written as fixed colors rather than theme tokens
 * on purpose: this is a preview of somebody else's page, not a surface of
 * this one, so it must not follow whichever way this admin has their own app
 * lit. A white wordmark uploaded into the light slot shows up as invisible
 * here, which is the point.
 */
function LogoTile({
  shape,
  theme,
  url,
  standIn,
  disabled,
  onPick,
  onClear,
  busy,
}: {
  shape: LogoShape;
  theme: LogoTheme;
  url: string | null;
  /** What is being used in this slot's place while it is empty, if anything. */
  standIn: string | null;
  disabled: boolean;
  onPick: (file: File) => void;
  onClear: () => void;
  busy: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const square = shape === 'square';
  const shown = url ?? standIn;

  return (
    <div className="space-y-2">
      <p className="text-caption font-medium text-chalk-dim">{square ? 'Square' : 'Landscape'}</p>
      <div
        className={cx(
          'flex items-center justify-center overflow-hidden rounded-md border border-dashed',
          theme === 'dark' ? 'border-white/20 bg-[#0b0d10]' : 'border-black/15 bg-white',
          square ? 'h-28 w-28' : 'h-28 w-full max-w-xs',
        )}
      >
        {busy ? (
          <Spinner className={theme === 'dark' ? 'text-white/50' : 'text-black/40'} />
        ) : shown ? (
          <img
            src={shown}
            alt={`${square ? 'Square' : 'Landscape'} logo for ${theme} pages`}
            className={cx('max-h-full max-w-full object-contain p-2', url ? '' : 'opacity-60')}
          />
        ) : (
          <span
            className={cx(
              'px-3 text-center text-caption',
              theme === 'dark' ? 'text-white/40' : 'text-black/40',
            )}
          >
            {square ? '1:1, 512px' : 'Wide, about 4:1'}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={disabled || busy} onClick={() => input.current?.click()}>
          {url ? 'Replace' : 'Upload'}
        </Button>
        {url ? (
          <button
            type="button"
            className="text-caption text-chalk-dim hover:text-chalk disabled:opacity-40"
            disabled={disabled || busy}
            onClick={onClear}
          >
            Remove
          </button>
        ) : null}
      </div>
      {/* Empty but standing on something: say so, so a faded logo in the tile
          is not read as a logo that has been uploaded and looks wrong. */}
      {!url && shown ? <p className="text-caption text-chalk-faint">Using another one for now</p> : null}
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Same input can pick the same file twice in a row; clearing the
          // value is what makes the second time fire a change at all.
          event.target.value = '';
          if (!file) return;
          const why = logoProblem(file);
          setProblem(why);
          if (!why) onPick(file);
        }}
      />
      <ErrorText>{problem}</ErrorText>
    </div>
  );
}

/** The two slots for one lighting, under a heading that says which. */
function LogoSet({
  theme,
  logos,
  locked,
  busySlot,
  onPick,
  onClear,
}: {
  theme: LogoTheme;
  logos: BrandLogos;
  locked: boolean;
  busySlot: string | null;
  onPick: (slot: LogoSlot, file: File) => void;
  onClear: (slot: LogoSlot) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-body font-medium text-chalk">{theme === 'dark' ? 'On dark pages' : 'On light pages'}</h3>
        <p className="text-caption text-chalk-faint">
          {theme === 'dark'
            ? 'Drawn to read on near-black. This is Slate’s own lighting.'
            : 'Drawn to read on white. Emails and most link previews land here whatever your pages do.'}
        </p>
      </div>
      <div className="flex flex-wrap items-start gap-6">
        {(['wide', 'square'] as const).map((shape) => {
          const slot: LogoSlot = { shape, theme };
          return (
            <LogoTile
              key={shape}
              shape={shape}
              theme={theme}
              url={logos[theme][shape]}
              standIn={logos[theme][shape] ? null : logoFor(logos, theme, shape)}
              disabled={locked}
              busy={busySlot === slotKey(slot)}
              onPick={(file) => onPick(slot, file)}
              onClear={() => onClear(slot)}
            />
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

/**
 * Three ways a share page can be lit. `auto` is the one that makes two sets
 * of logos worth having: the page follows whoever is looking at it, so both
 * lightings are in play on the same team's links.
 */
const THEME_CHOICES: { value: BrandTheme; label: string }[] = [
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
  { value: 'auto', label: 'Follow the viewer' },
];

/** A dot of the page color; split down the middle for `auto`. */
function ThemeSwatch({ theme }: { theme: BrandTheme }) {
  if (theme === 'auto') {
    return (
      <span
        aria-hidden
        className="flex h-3.5 w-3.5 overflow-hidden rounded-full border border-slate-600"
      >
        <span className="h-full w-1/2 bg-[#0b0d10]" />
        <span className="h-full w-1/2 bg-white" />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className={cx(
        'h-3.5 w-3.5 rounded-full border border-slate-600',
        theme === 'light' ? 'bg-white' : 'bg-[#0b0d10]',
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Slates
// ---------------------------------------------------------------------------

/**
 * Picking a recording to play before or after every other one.
 *
 * The list is the team's own library, which is where these come from: a slate
 * is a recording somebody already made and shared, not a separate kind of
 * thing to upload.
 *
 * The search goes to the API rather than filtering what was already fetched:
 * a team with three hundred recordings would otherwise be searching the most
 * recent hundred and quietly missing the one they made the slate in. It also
 * means the transcript is searched, so "the one where I said welcome" finds
 * it without anybody having titled it that.
 *
 * Nothing is fetched until the picker is open. Most visits to this page are
 * about a logo or a color.
 */
function SlatePicker({
  orgId,
  label,
  description,
  current,
  disabled,
  onChoose,
  onClear,
}: {
  orgId: string;
  label: string;
  description: string;
  current: BrandingSlateRef | null;
  disabled: boolean;
  onChoose: (videoId: string) => void;
  onClear: () => void;
}) {
  const [picking, setPicking] = useState(false);
  const [term, setTerm] = useState('');
  // Typing should not be a request per keystroke; a beat of quiet is enough.
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(term.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  const library = useQuery({
    queryKey: qk.videos(`branding-slates:${query}`),
    queryFn: () => fetchLibrary({ limit: 100, q: query || undefined }),
    enabled: picking && orgId !== '',
    staleTime: 30_000,
  });
  // A slate has to be a recording this team owns and that will actually play:
  // somebody else's, an audio note, one still processing or one behind the
  // free-tier window would all be a broken few seconds in front of every
  // recording the team shares.
  const choices = (library.data?.videos ?? []).filter(
    (video: VideoSummary) =>
      video.orgId === orgId && video.status === 'ready' && video.kind !== 'audio' && !video.locked,
  );
  const loading = library.isPending;

  return (
    <div className="rounded-md border border-hairline bg-slate-900/40 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-body text-chalk">{label}</p>
          <p className="mt-0.5 text-caption text-chalk-faint">{description}</p>
        </div>
        {current && !picking ? (
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" disabled={disabled} onClick={() => setPicking(true)}>
              Change
            </Button>
            <button
              type="button"
              className="text-caption text-chalk-dim hover:text-chalk disabled:opacity-40"
              disabled={disabled}
              onClick={onClear}
            >
              Remove
            </button>
          </div>
        ) : !picking ? (
          <Button size="sm" disabled={disabled} onClick={() => setPicking(true)}>
            Choose
          </Button>
        ) : (
          <button
            type="button"
            className="shrink-0 text-caption text-chalk-dim hover:text-chalk"
            onClick={() => setPicking(false)}
          >
            Cancel
          </button>
        )}
      </div>

      {current && !picking ? (
        <div className="mt-3 flex items-center gap-3">
          <div className="h-12 w-20 shrink-0 overflow-hidden rounded bg-slate-900">
            {current.posterUrl ? (
              <img src={current.posterUrl} alt="" className="h-full w-full object-cover" />
            ) : null}
          </div>
          <div className="min-w-0">
            <AppLink to={`/v/${current.shareId}`} className="block truncate text-body text-chalk hover:underline">
              {current.title}
            </AppLink>
            <p className="text-caption text-chalk-faint">
              {current.durationSec ? <span className="tnum font-mono">{timecode(current.durationSec)}</span> : null}
              {!current.playable ? (
                <span className="ml-2 text-caution">Not ready, so it is skipped</span>
              ) : null}
            </p>
          </div>
        </div>
      ) : null}

      {picking ? (
        <>
          <div className="mt-3">
            <Input
              type="search"
              placeholder="Search the team’s recordings"
              value={term}
              autoFocus
              spellCheck={false}
              onChange={(event) => setTerm(event.target.value)}
              aria-label={`Search recordings to use as the ${label.toLowerCase()}`}
            />
          </div>
          {loading ? (
            <div className="mt-3">
              <Loading label="Loading the team’s recordings" />
            </div>
          ) : choices.length === 0 ? (
            <p className="mt-3 text-caption text-chalk-faint">
              {query
                ? `Nothing in the team library matches “${query}”.`
                : 'Nothing in the team library yet. Record the slate first, then come back and choose it here.'}
            </p>
          ) : (
            <ul className="mt-3 max-h-64 space-y-1 overflow-y-auto pr-1">
              {choices.map((video) => (
                <li key={video.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded p-1.5 text-left hover:bg-slate-800"
                    onClick={() => {
                      onChoose(video.id);
                      setPicking(false);
                    }}
                  >
                    <span className="h-10 w-16 shrink-0 overflow-hidden rounded bg-slate-900">
                      {video.posterUrl ? <img src={video.posterUrl} alt="" className="h-full w-full object-cover" /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body text-chalk">{video.title}</span>
                      {video.durationSec ? (
                        <span className="text-caption tnum font-mono text-chalk-faint">{timecode(video.durationSec)}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

/**
 * A share page in miniature, lit and colored exactly as a viewer's would be.
 *
 * It is driven by the same two mechanisms the real page uses, not by a
 * lookalike: `data-theme` switches the palette every Tailwind class in here
 * reads from, and `--brand` / `--brand-ink` feed the same `brand-fill` and
 * `brand-play` classes the player itself carries. So this cannot drift from
 * what it is previewing without the real page drifting too.
 *
 * It follows the draft rather than what is saved, so a color being typed
 * shows up as it is typed and the Save button is a commitment rather than a
 * way of finding out what something looks like.
 */
function BrandingPreview({
  orgName,
  theme,
  brandColor,
  brandInk,
  logos,
  hideSlateBranding,
  hasIntro,
}: {
  orgName: string;
  /** The lighting being previewed, already resolved: never 'auto'. */
  theme: LogoTheme;
  brandColor: string | null;
  brandInk: string | null;
  logos: BrandLogos;
  hideSlateBranding: boolean;
  hasIntro: boolean;
}) {
  const logo = logoFor(logos, theme, 'wide');
  return (
    <div
      data-theme={theme}
      style={
        brandColor
          ? ({ '--brand': brandColor, '--brand-ink': brandInk ?? '#0b0d10' } as CSSProperties)
          : undefined
      }
      className="overflow-hidden rounded-md border border-hairline bg-slate-950"
      aria-label="Preview of a share page"
    >
      <div className="flex h-10 items-center justify-between border-b border-hairline px-3">
        {logo ? (
          <img src={logo} alt="" className="max-h-5 w-auto max-w-[120px] object-contain" />
        ) : (
          <span className="text-caption font-medium text-chalk">Slate</span>
        )}
        <span className="rounded border border-hairline bg-slate-800 px-2 py-0.5 text-micro text-chalk-dim">
          Log in
        </span>
      </div>

      <div className="space-y-2.5 p-3">
        <div>
          <p className="text-body font-medium text-chalk">How to reset your password</p>
          <p className="text-micro text-chalk-faint">{orgName} · 2 min</p>
        </div>

        {/* The player keeps its own dark surface on a light page, exactly as
            it does on the real one. */}
        <div data-theme="dark" className="relative overflow-hidden rounded bg-black">
          <div className="aspect-video w-full bg-gradient-to-br from-slate-800 to-black" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="brand-play flex h-9 w-9 items-center justify-center rounded-full">
              <PlayIcon size={15} />
            </span>
          </div>
          {hasIntro ? (
            <span className="absolute bottom-1.5 right-1.5 rounded border border-white/25 bg-slate-950/70 px-1.5 py-0.5 text-micro text-chalk">
              Skip intro
            </span>
          ) : null}
          <div className="absolute inset-x-2 bottom-2 h-1 rounded-full bg-chalk/20">
            <div className="brand-fill h-1 w-1/3 rounded-full" />
          </div>
        </div>

        <div className="rounded border border-hairline bg-slate-900/40 px-2.5 py-1.5">
          <p className="text-micro uppercase tracking-wide text-chalk-faint">Summary</p>
          <div className="mt-1 space-y-1" aria-hidden>
            <div className="h-1.5 w-11/12 rounded bg-chalk/15" />
            <div className="h-1.5 w-3/5 rounded bg-chalk/15" />
          </div>
        </div>
      </div>

      {hideSlateBranding ? null : (
        <div className="flex items-center justify-between border-t border-hairline px-3 py-2 text-micro text-chalk-faint">
          <span>Recorded with Slate</span>
          <span>Make your own →</span>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------

/**
 * The invitation email, in miniature.
 *
 * The email is the one surface a team never sees: they do not receive their
 * own invitations, so a logo that vanishes into their own colour can go
 * unnoticed for as long as it takes somebody to mention it. The layout here
 * mirrors services/notify.ts in slate-api - colour as the rule above and on
 * the button, logo on white - so what they see is what arrives.
 */
function EmailPreview({
  orgName,
  brandColor,
  brandInk,
  logos,
}: {
  orgName: string;
  brandColor: string | null;
  brandInk: string | null;
  logos: BrandLogos;
}) {
  // The band is white whatever the pages do, so this is the light set.
  const logo = logoFor(logos, 'light', 'wide');
  const accent = brandColor ?? '#0b0d10';
  return (
    <div className="overflow-hidden rounded-md border border-hairline bg-[#f1f3f5] p-3">
      <div className="overflow-hidden rounded bg-white shadow-sm">
        <div style={{ background: accent, height: 5 }} />
        <div className="border-b border-[#e3e7eb] px-3 py-2.5">
          {logo ? (
            <img src={logo} alt="" className="h-5 w-auto max-w-[110px] object-contain" />
          ) : (
            <span className="text-caption font-semibold text-[#111418]">Slate</span>
          )}
        </div>
        <div className="space-y-1.5 px-3 py-3">
          <p className="text-micro text-[#111418]">Hi,</p>
          <p className="text-micro leading-relaxed text-[#111418]">
            You have been invited to join {orgName} on Slate.
          </p>
          <span
            className="mt-1 inline-flex h-6 items-center rounded px-2.5 text-micro font-semibold"
            style={{ background: accent, color: brandInk ?? '#ffffff' }}
          >
            Join {orgName}
          </span>
        </div>
        <div className="border-t border-[#e3e7eb] px-3 py-1.5 text-micro text-[#8a94a0]">
          {orgName} · sent with Slate
        </div>
      </div>
    </div>
  );
}

/**
 * What the share preview is showing, said plainly: whether this is the page
 * every viewer gets, one of the two they may get, or a lighting the team has
 * turned off and is only looking at.
 */
function previewNote(theme: BrandTheme, shown: LogoTheme): string {
  if (theme === 'auto') {
    return `A share page as a viewer whose computer is ${shown} sees it.`;
  }
  if (theme === shown) return 'A share page as a viewer sees it.';
  return `Your pages are ${theme}. This is what a ${shown} one would look like.`;
}

export function BrandingPage() {
  const { org, loading: orgLoading } = useOrg();
  const client = useQueryClient();
  const orgId = org?.id ?? '';

  const query = useQuery({
    queryKey: qk.branding(orgId),
    queryFn: () => fetchBranding(orgId),
    enabled: orgId !== '',
  });
  const branding = query.data ?? null;
  const settled = (next: BrandingSettings) => client.setQueryData(qk.branding(orgId), next);

  const [color, setColor] = useState('');
  const [preview, setPreview] = useState<'share' | 'email'>('share');
  /**
   * Which lighting the share preview is showing. It follows the team's theme
   * when they have chosen one, and starts dark on `auto`; either way it can
   * be switched, because checking that both logos read is the whole reason
   * there are two of them.
   */
  const [previewTheme, setPreviewTheme] = useState<LogoTheme | null>(null);
  useEffect(() => {
    if (branding) setColor(branding.brandColor ?? '');
  }, [branding]);

  const save = useMutation({
    mutationFn: (body: Parameters<typeof updateBranding>[1]) => updateBranding(orgId, body),
    onSuccess: settled,
  });
  const logo = useMutation({
    mutationFn: ({ slot, file }: { slot: LogoSlot; file: File }) => uploadLogo(orgId, slot, file),
    onSuccess: settled,
  });
  const clearLogo = useMutation({
    mutationFn: (slot: LogoSlot) => removeLogo(orgId, slot),
    onSuccess: settled,
  });

  if (orgLoading || (orgId !== '' && query.isLoading)) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="branding" />
        <Loading label="Loading your branding" />
      </>
    );
  }

  if (!org) {
    return (
      <>
        <PageHeader title="Settings" subtitle="Your account, your plan and your team." />
        <SettingsTabs active="branding" />
        <EmptyState title="Branding belongs to a team">
          Your logo, color and slates go on everything the team shares, so there has to be a team first.
          <div className="mt-4">
            <AppLink to="/settings/subscription">
              <Button variant="primary">Start a team</Button>
            </AppLink>
          </div>
        </EmptyState>
      </>
    );
  }

  if (query.isError || !branding) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="branding" />
        <Banner tone="danger" title="That did not load">{errorMessage(query.error)}</Banner>
      </>
    );
  }

  const locked = !branding.mayManage || !branding.included;
  /** Both sets uploaded: nothing is standing in for anything. */
  const bothSets =
    Boolean(branding.logos.dark.square || branding.logos.dark.wide) &&
    Boolean(branding.logos.light.square || branding.logos.light.wide);
  /** The lighting the preview draws: the switch, else the team's choice. */
  const shownTheme: LogoTheme = previewTheme ?? (branding.theme === 'light' ? 'light' : 'dark');
  const colorValid = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color.trim());
  /** What both previews draw with: the colour being typed, not the saved one. */
  const draftColor = colorValid ? (color.startsWith('#') ? color.toLowerCase() : `#${color.toLowerCase()}`) : null;
  const colorDirty = (branding.brandColor ?? '') !== (color.trim() ? color.trim().toLowerCase() : '');

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle={`How ${org.name} looks to everyone you share a recording with.`}
      />
      <SettingsTabs active="branding" />

      {/* Wide: the preview keeps pace beside the settings, so the effect of a
          color or a theme is visible at the moment it is chosen rather than
          after a trip to a share page. Narrow: it sits above them, where it
          is still the first thing seen. */}
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start lg:gap-8">
        <div className="order-1 space-y-6">
        {!branding.included ? (
          <Banner
            tone="caution"
            title="Custom branding comes with the team plan"
            action={
              <AppLink to="/settings/subscription">
                <Button size="sm" variant="primary">
                  See the plan
                </Button>
              </AppLink>
            }
          >
            Everything set here is kept exactly as it is. Share pages show Slate's own look until the plan is running
            again, and yours the moment it is.
          </Banner>
        ) : !branding.mayManage ? (
          <Banner tone="info" title="Only admins and owners can change this">
            This is what {org.name} looks like on every recording the team shares.
          </Banner>
        ) : null}

        <Section
          title="Logos"
          description="The landscape logo stands where the Slate wordmark does, at the top of every share page. The square one is used anywhere small: the browser tab, and the preview when a link is pasted into a chat. Upload a version for each way a page can be lit, since a mark drawn for a dark page usually disappears on a white one."
        >
          <div className="space-y-6">
            {(['dark', 'light'] as const).map((set) => (
              <LogoSet
                key={set}
                theme={set}
                logos={branding.logos}
                locked={locked}
                busySlot={logo.isPending && logo.variables ? slotKey(logo.variables.slot) : null}
                onPick={(slot, file) => logo.mutate({ slot, file })}
                onClear={(slot) => clearLogo.mutate(slot)}
              />
            ))}
          </div>
          {/* One set is enough to be branded; the other is an improvement,
              not a requirement, and nothing here blocks on it. */}
          {hasAnyLogo(branding.logos) && !bothSets ? (
            <p className="mt-4 text-caption text-chalk-faint">
              The set you have uploaded is being used in both places until the other one arrives.
            </p>
          ) : null}
          {logo.isError ? (
            <div className="mt-3">
              <ErrorText>{errorMessage(logo.error)}</ErrorText>
            </div>
          ) : null}
        </Section>

        <Section
          title="Brand color"
          description="Buttons, highlights and the player's progress bar on your share pages, and the header in emails."
        >
          {/* Just the color and Save. What it looks like is the preview
              beside the page, which shows it on a real share page rather
              than on a sample button; emptying the field and saving is how
              it comes off. */}
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (colorValid || color.trim() === '') save.mutate({ brandColor: color.trim() || null });
            }}
          >
            <label className="relative h-9 w-9 shrink-0 cursor-pointer overflow-hidden rounded border border-hairline">
              <input
                type="color"
                value={colorValid ? (color.startsWith('#') ? color : `#${color}`) : '#1a6e64'}
                disabled={locked}
                onChange={(event) => setColor(event.target.value)}
                // Blown up and clipped by the label: the native swatch has
                // its own padding, and a chip of color should be all color.
                className="absolute left-[-25%] top-[-25%] h-[150%] w-[150%] cursor-pointer border-0 bg-transparent p-0"
                aria-label="Pick a brand color"
              />
            </label>
            <input
              value={color}
              placeholder="#1a6e64"
              disabled={locked}
              spellCheck={false}
              maxLength={7}
              aria-label="Brand color as hex"
              aria-invalid={color.trim() !== '' && !colorValid ? true : undefined}
              onChange={(event) => setColor(event.target.value)}
              className={cx(
                'h-9 w-32 rounded border bg-slate-900 px-2.5 font-mono text-body text-chalk transition-colors',
                'placeholder:text-chalk-faint hover:border-slate-600 focus:border-signal focus:outline-none disabled:opacity-50',
                color.trim() !== '' && !colorValid ? 'border-danger/60' : 'border-hairline',
              )}
            />
            <Button
              type="submit"
              variant="primary"
              size="sm"
              busy={save.isPending}
              disabled={locked || !colorDirty || (color.trim() !== '' && !colorValid)}
            >
              Save
            </Button>
          </form>
          {color.trim() !== '' && !colorValid ? (
            <div className="mt-2">
              <ErrorText>Looks like #1a6e64.</ErrorText>
            </div>
          ) : null}
          {save.isError ? (
            <div className="mt-3">
              <ErrorText>{errorMessage(save.error)}</ErrorText>
            </div>
          ) : null}
        </Section>

        <Section
          title="Theme"
          description="How your share pages are lit. The player itself stays dark whichever you pick: its controls sit on top of the footage."
        >
          <div className="flex flex-wrap gap-2">
            {THEME_CHOICES.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                disabled={locked || save.isPending}
                aria-pressed={branding.theme === value}
                onClick={() => save.mutate({ theme: value })}
                className={cx(
                  'flex h-9 items-center gap-2 rounded border px-3 text-body transition-colors duration-120',
                  'disabled:opacity-40 disabled:pointer-events-none',
                  branding.theme === value
                    ? 'border-chalk/25 bg-slate-700 text-chalk'
                    : 'border-hairline bg-slate-900 text-chalk-dim hover:border-slate-600 hover:text-chalk',
                )}
              >
                <ThemeSwatch theme={value} />
                {label}
              </button>
            ))}
          </div>
          {branding.theme === 'auto' ? (
            <p className="mt-3 text-caption text-chalk-faint">
              A viewer who keeps their computer light gets a light page, and one who keeps it dark gets a dark one.
              Upload both logos so your mark reads either way.
            </p>
          ) : null}
        </Section>

        <Section
          title="Intro and outro"
          description="Recordings from the team library, played around every recording the team shares. They apply to everything already recorded, and changing one takes effect straight away."
        >
          <div className="space-y-3">
            <SlatePicker
              orgId={org.id}
              label="Intro"
              description="Plays before the recording. Viewers can skip it."
              current={branding.intro}
              disabled={locked}
              onChoose={(introVideoId) => save.mutate({ introVideoId })}
              onClear={() => save.mutate({ introVideoId: null })}
            />
            <SlatePicker
              orgId={org.id}
              label="Outro"
              description="Plays once the recording finishes, before the end card."
              current={branding.outro}
              disabled={locked}
              onChoose={(outroVideoId) => save.mutate({ outroVideoId })}
              onClear={() => save.mutate({ outroVideoId: null })}
            />
          </div>
          <p className="mt-3 text-caption text-chalk-faint">
            Slates are played by the share page, so a downloaded MP4 is still just the recording.
          </p>
        </Section>

        <Section
          title="Slate's name"
          description="Every share page carries a quiet line crediting Slate and a link offering to make your own. You can take both off."
        >
          <label className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={branding.hideSlateBranding}
              aria-label="Remove Slate branding from share pages"
              disabled={locked || save.isPending}
              onClick={() => save.mutate({ hideSlateBranding: !branding.hideSlateBranding })}
              className={cx(
                'relative h-5 w-9 shrink-0 rounded-full border transition-colors duration-120',
                'disabled:opacity-40 disabled:pointer-events-none',
                branding.hideSlateBranding ? 'border-go bg-go' : 'border-slate-600 bg-slate-800',
              )}
            >
              <span
                className={cx(
                  'absolute left-0.5 top-0.5 h-3.5 w-3.5 rounded-full shadow-sm transition-transform duration-120',
                  branding.hideSlateBranding ? 'translate-x-4 bg-white' : 'translate-x-0 bg-chalk-dim',
                )}
              />
            </button>
            <span className="text-body text-chalk">Remove Slate branding</span>
          </label>
        </Section>

        </div>

        {/* `lg:order-last` rather than `lg:order-none`: the settings carry
            `order-1`, so an aside at order 0 was landing in the first grid
            column, which is the wide one, and squeezing the settings into the
            320px track meant for the preview. */}
        <aside className="order-first mb-6 lg:sticky lg:top-6 lg:order-last lg:mb-0">
          <div className="mb-2 flex items-center gap-3">
            <h2 className="text-caption font-medium uppercase tracking-wide text-chalk-faint">Preview</h2>
            {/* Two surfaces, because they use the colour differently and a team
                sees neither of them by default: nobody receives their own
                invitations. */}
            <div className="flex gap-1">
              {(['share', 'email'] as const).map((surface) => (
                <button
                  key={surface}
                  type="button"
                  aria-pressed={preview === surface}
                  onClick={() => setPreview(surface)}
                  className={cx(
                    'rounded px-2 py-0.5 text-caption capitalize transition-colors duration-120',
                    preview === surface ? 'bg-slate-700 text-chalk' : 'text-chalk-dim hover:text-chalk',
                  )}
                >
                  {surface === 'share' ? 'Share page' : 'Email'}
                </button>
              ))}
            </div>
          </div>
          {/* Which lighting the share page is being drawn in. It starts on the
              team's own choice, and switching it is how they check that the
              other logo reads: on `auto` both of these are real pages that
              real viewers get. The email has no switch; its band is white. */}
          {preview === 'share' ? (
            <div className="mb-2 flex gap-1" role="group" aria-label="Preview lighting">
              {(['dark', 'light'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={shownTheme === option}
                  onClick={() => setPreviewTheme(option)}
                  className={cx(
                    'flex items-center gap-1.5 rounded px-2 py-0.5 text-caption capitalize transition-colors duration-120',
                    shownTheme === option ? 'bg-slate-700 text-chalk' : 'text-chalk-dim hover:text-chalk',
                  )}
                >
                  <ThemeSwatch theme={option} />
                  {option}
                </button>
              ))}
            </div>
          ) : null}
          {preview === 'share' ? (
            <BrandingPreview
              orgName={org.name}
              theme={shownTheme}
              // The draft color, so it shows while it is being typed.
              brandColor={draftColor}
              brandInk={colorValid ? inkFor(color) : null}
              logos={branding.logos}
              hideSlateBranding={branding.hideSlateBranding}
              hasIntro={branding.intro !== null}
            />
          ) : (
            <EmailPreview
              orgName={org.name}
              brandColor={draftColor}
              brandInk={colorValid ? inkFor(color) : null}
              logos={branding.logos}
            />
          )}
          <p className="mt-2 text-caption text-chalk-faint">
            {preview === 'share'
              ? previewNote(branding.theme, shownTheme)
              : 'An invitation as it arrives. The band is white whatever your pages do, so this is where the light logo goes.'}
          </p>
        </aside>
      </div>
    </>
  );
}
